import {
  CheckCircleOutlined,
  CloudUploadOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  FormOutlined,
  InfoCircleOutlined,
  UploadOutlined,
  LeftOutlined,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Popover,
  Select,
  Table,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import {
  type ClipboardEvent,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useBlocker, useNavigate } from "react-router-dom";

import { ApiError } from "../api/client";
import { completeUpload, createUploadIntent, uploadToIntent } from "../api/attachments";
import {
  downloadBasicProjectImportTemplate,
  validateBasicProjectImport,
  type BasicProjectImportError,
  type BasicProjectImportValidationResult,
} from "../api/projectImports";
import {
  batchDeleteProjects,
  batchUpdateProjectStatus,
  createProject,
  deleteProject,
  fetchProjectMetadata,
  fetchProjects,
  restoreProject,
  updateProjectStatus,
  type ProjectDetail,
  type ProjectListItem,
  type ProjectReviewAssignmentStatus,
  type ProjectStatus,
  type ProjectWritePayload,
} from "../api/projects";
import { fetchRegistrants, type Registrant } from "../api/registrants";
import { createReviewAssignments, fetchReviewerOptions } from "../api/reviews";
import { fetchUsers } from "../api/users";
import { useAuth } from "../features/auth/AuthContext";
import { ProjectLibraryView } from "../features/projects/ProjectLibraryView";
import { ReviewerAssignmentModal } from "../features/projects/ReviewerAssignmentModal";

interface ProjectFormValues {
  work_name: string;
  work_type_code: string;
  rights_mode_code: string;
  completion_date: string;
  publication_date?: string;
  author_ids: string[];
  right_holder_ids: string[];
  agent_name?: string;
  contact_phone?: string;
  cloud_drive_info?: string;
  work_summary?: string;
  creation_process?: string;
}

interface ProjectBatchRow extends ProjectFormValues {
  id: string;
}

type BatchPartyField = "right_holder_ids" | "author_ids";
type BatchSyncField =
  | "work_type_code"
  | "rights_mode_code"
  | "completion_date"
  | "publication_date"
  | "right_holder_ids"
  | "author_ids";

interface BatchPartyPickerState {
  source: "single" | "batch";
  rowId?: string;
  field: BatchPartyField;
  title: "著作权人" | "作者";
  maxCount: 1 | 10;
}

type BatchInlineTextField = "work_summary" | "creation_process" | "cloud_drive_info";

type BatchDraftSaveStatus = "saved" | "saving" | "error";

interface BatchStagedAttachment {
  id: string;
  file: File;
}

const STATUS_LABELS: Record<ProjectStatus, string> = {
  SUBMITTED: "已提交",
  COMPLETED: "已完成",
};
const PROJECT_LONG_TEXT_LIMIT = 1500;
const PROJECT_CLOUD_TEXT_LIMIT = 500;
const BATCH_DRAFT_STORAGE_KEY = "project-registration-batch-draft";
const BATCH_DRAFT_SAVE_DEBOUNCE_MS = 800;
const BATCH_DRAFT_SAVE_MAX_WAIT_MS = 3_000;
const LEGACY_WORK_TYPE_CODE_ALIASES: Record<string, string> = {
  DRAMA_WORK: "PHOTOGRAPHIC_WORK",
  DANCE_WORK: "QUYI_WORK",
};
const BASIC_IMPORT_FIELD_LABELS: Record<string, string> = {
  work_name: "作品名称",
  work_type_code: "作品类型",
  rights_mode_code: "权利归属方式",
  completion_date: "创作完成时间",
  publication_date: "发表日期",
  work_summary: "作品简介及创意",
  creation_process: "创作经过及意图",
  cloud_drive_info: "网盘信息",
};
const BATCH_ATTACHMENT_ACCEPT =
  ".png,.jpg,.webp,.mp3,.mp4,.wav,.rar,.zip,.docx,.doc,.pdf,.xls,.xlsx";
const BATCH_ATTACHMENT_EXTENSIONS = new Set(
  BATCH_ATTACHMENT_ACCEPT.split(",").map((extension) => extension.slice(1)),
);
const MAX_BATCH_ATTACHMENTS = 20;
const MAX_BATCH_ATTACHMENT_SIZE = 100 * 1024 * 1024;

function getProjectPartyLimit(rightsModeCode: string): 1 | 10 {
  return rightsModeCode === "COOPERATIVE_WORK" ? 10 : 1;
}

function normalizeWorkTypeCode(code: string): string {
  return LEGACY_WORK_TYPE_CODE_ALIASES[code] ?? code;
}

interface PartySelectionControlProps {
  id?: string;
  value?: string[];
  title: "著作权人" | "作者";
  partyNameById: Map<string, string>;
  onOpen: () => void;
}

function PartySelectionControl({
  id,
  value = [],
  title,
  partyNameById,
  onOpen,
}: PartySelectionControlProps) {
  if (!value.length) {
    return (
      <Button
        id={id}
        className="batch-party-add-button single-party-add-button"
        block
        icon={<PlusOutlined />}
        onClick={onOpen}
      >
        添加{title}
      </Button>
    );
  }
  return (
    <button
      id={id}
      className="batch-party-selection single-party-selection"
      type="button"
      onClick={onOpen}
    >
      {value.map((registrantId) => {
        const name = partyNameById.get(registrantId) ?? "登记人已失效";
        return (
          <span key={registrantId} title={name}>
            {name}
          </span>
        );
      })}
    </button>
  );
}

function hasBatchRowData(row: ProjectBatchRow): boolean {
  return (
    [
      row.work_name,
      row.work_type_code,
      row.rights_mode_code,
      row.completion_date,
      row.publication_date,
      row.cloud_drive_info,
      row.work_summary,
      row.creation_process,
    ].some((value) => Boolean(value?.trim())) ||
    row.author_ids.length > 0 ||
    row.right_holder_ids.length > 0
  );
}

function isBatchRowSubmittable(row: ProjectBatchRow): boolean {
  const partyLimit = getProjectPartyLimit(row.rights_mode_code);
  const workSummary = row.work_summary ?? "";
  const creationProcess = row.creation_process ?? "";
  const cloudDriveInfo = row.cloud_drive_info ?? "";
  return (
    Boolean(row.work_name.trim()) &&
    Boolean(row.work_type_code) &&
    Boolean(row.rights_mode_code) &&
    Boolean(row.completion_date) &&
    Boolean(workSummary.trim()) &&
    Boolean(creationProcess.trim()) &&
    workSummary.length <= PROJECT_LONG_TEXT_LIMIT &&
    creationProcess.length <= PROJECT_LONG_TEXT_LIMIT &&
    cloudDriveInfo.length <= PROJECT_CLOUD_TEXT_LIMIT &&
    row.author_ids.length > 0 &&
    row.right_holder_ids.length > 0 &&
    row.author_ids.length <= partyLimit &&
    row.right_holder_ids.length <= partyLimit
  );
}

function createRowId() {
  return (
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}

function createEmptyBatchRow(): ProjectBatchRow {
  return {
    id: createRowId(),
    work_name: "",
    work_type_code: "",
    rights_mode_code: "",
    completion_date: "",
    publication_date: "",
    author_ids: [],
    right_holder_ids: [],
    agent_name: "",
    contact_phone: "",
    cloud_drive_info: "",
    work_summary: "",
    creation_process: "",
  };
}

function normalizeBatchRows(value: unknown): ProjectBatchRow[] {
  if (!Array.isArray(value)) return [];
  return value.map((row) => {
    const source = typeof row === "object" && row !== null ? (row as Record<string, unknown>) : {};
    const stringArray = (field: string) =>
      Array.isArray(source[field])
        ? source[field].filter((item): item is string => typeof item === "string")
        : [];
    const text = (field: string) => (typeof source[field] === "string" ? source[field] : "");

    const rightsModeCode = text("rights_mode_code");
    const partyLimit = getProjectPartyLimit(rightsModeCode);
    return {
      ...createEmptyBatchRow(),
      work_name: text("work_name"),
      work_type_code: normalizeWorkTypeCode(text("work_type_code")),
      rights_mode_code: rightsModeCode,
      completion_date: text("completion_date"),
      publication_date: text("publication_date"),
      author_ids: stringArray("author_ids").slice(0, partyLimit),
      right_holder_ids: stringArray("right_holder_ids").slice(0, partyLimit),
      agent_name: "",
      contact_phone: "",
      cloud_drive_info: text("cloud_drive_info"),
      work_summary: text("work_summary"),
      creation_process: text("creation_process"),
      id: createRowId(),
    };
  });
}

function loadBatchDraft(): ProjectBatchRow[] {
  if (typeof localStorage === "undefined") {
    return Array.from({ length: 6 }, () => createEmptyBatchRow());
  }
  try {
    const rows = normalizeBatchRows(
      JSON.parse(localStorage.getItem(BATCH_DRAFT_STORAGE_KEY) ?? "[]"),
    );
    return rows.length ? rows : Array.from({ length: 6 }, () => createEmptyBatchRow());
  } catch {
    return Array.from({ length: 6 }, () => createEmptyBatchRow());
  }
}

function errorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return fallback;
}

function displayImportValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value) ?? "-";
}

function nullable(value?: string): string | null {
  const normalized = value?.trim();
  return normalized || null;
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function toProjectPayload(values: ProjectFormValues): ProjectWritePayload {
  return {
    work_name: values.work_name.trim(),
    work_type_code: values.work_type_code,
    rights_mode_code: values.rights_mode_code,
    completion_date: values.completion_date,
    publication_date: values.publication_date || null,
    author_ids: values.author_ids,
    right_holder_ids: values.right_holder_ids,
    agent_name: nullable(values.agent_name),
    contact_phone: nullable(values.contact_phone),
    cloud_drive_info: nullable(values.cloud_drive_info),
    work_summary: (values.work_summary ?? "").trim(),
    creation_process: (values.creation_process ?? "").trim(),
  };
}

export function ProjectManagementPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form] = Form.useForm<ProjectFormValues>();
  const singleCompletionDate = Form.useWatch("completion_date", form);
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [batchChoiceOpen, setBatchChoiceOpen] = useState(false);
  const [batchWorkspaceOpen, setBatchWorkspaceOpen] = useState(false);
  const [batchRows, setBatchRows] = useState<ProjectBatchRow[]>(() => loadBatchDraft());
  const [batchDraftSaveStatus, setBatchDraftSaveStatus] = useState<BatchDraftSaveStatus>("saved");
  const [batchDraftSavedAt, setBatchDraftSavedAt] = useState("");
  const batchRowsRef = useRef(batchRows);
  const batchDraftSaveInitializedRef = useRef(false);
  const batchDraftSavePriorityRef = useRef<"debounced" | "immediate">("debounced");
  const batchDraftDebounceTimerRef = useRef<number | undefined>(undefined);
  const batchDraftMaxWaitTimerRef = useRef<number | undefined>(undefined);
  const lastSavedBatchDraftRef = useRef(JSON.stringify(batchRows));
  const [selectedBatchRowIds, setSelectedBatchRowIds] = useState<string[]>([]);
  const [batchPartyPicker, setBatchPartyPicker] = useState<BatchPartyPickerState | null>(null);
  const [batchPartyDraftIds, setBatchPartyDraftIds] = useState<string[]>([]);
  const [batchPartySearch, setBatchPartySearch] = useState("");
  const [batchPartyPage, setBatchPartyPage] = useState(1);
  const [batchPartyPageSize, setBatchPartyPageSize] = useState(10);
  const [batchPartyRegistrantCache, setBatchPartyRegistrantCache] = useState<
    Record<string, Registrant>
  >({});
  const [batchAttachmentFiles, setBatchAttachmentFiles] = useState<
    Record<string, BatchStagedAttachment[]>
  >({});
  const [batchFile, setBatchFile] = useState<File>();
  const [basicImportResult, setBasicImportResult] = useState<BasicProjectImportValidationResult>();
  const [basicImportErrorsOpen, setBasicImportErrorsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ProjectStatus>();
  const [workTypeFilter, setWorkTypeFilter] = useState<string>();
  const [ownerUserId, setOwnerUserId] = useState<string>();
  const [reviewAssignmentStatus, setReviewAssignmentStatus] =
    useState<ProjectReviewAssignmentStatus>();
  const [submittedDate, setSubmittedDate] = useState("");
  const [recycleBin, setRecycleBin] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const navigationBlocker = useBlocker(batchWorkspaceOpen);

  const clearBatchDraftSaveTimers = useCallback(() => {
    if (batchDraftDebounceTimerRef.current !== undefined) {
      window.clearTimeout(batchDraftDebounceTimerRef.current);
      batchDraftDebounceTimerRef.current = undefined;
    }
    if (batchDraftMaxWaitTimerRef.current !== undefined) {
      window.clearTimeout(batchDraftMaxWaitTimerRef.current);
      batchDraftMaxWaitTimerRef.current = undefined;
    }
  }, []);

  const persistBatchDraft = useCallback((force = false) => {
    if (typeof localStorage === "undefined") {
      setBatchDraftSaveStatus("error");
      return false;
    }
    const serializedRows = JSON.stringify(batchRowsRef.current);
    if (!force && serializedRows === lastSavedBatchDraftRef.current) {
      setBatchDraftSaveStatus("saved");
      return true;
    }
    try {
      localStorage.setItem(BATCH_DRAFT_STORAGE_KEY, serializedRows);
      lastSavedBatchDraftRef.current = serializedRows;
      setBatchDraftSavedAt(dayjs().format("HH:mm:ss"));
      setBatchDraftSaveStatus("saved");
      return true;
    } catch {
      setBatchDraftSaveStatus("error");
      return false;
    }
  }, []);

  const flushBatchDraftSave = useCallback(
    (force = false) => {
      clearBatchDraftSaveTimers();
      batchDraftSavePriorityRef.current = "debounced";
      return persistBatchDraft(force);
    },
    [clearBatchDraftSaveTimers, persistBatchDraft],
  );

  const requestImmediateBatchDraftSave = useCallback(() => {
    clearBatchDraftSaveTimers();
    batchDraftSavePriorityRef.current = "immediate";
  }, [clearBatchDraftSaveTimers]);

  const confirmBatchWorkspaceLeave = useCallback(
    (onLeave: () => void, onStay?: () => void) =>
      modal.confirm({
        title: "确认离开批量创建项目？",
        content: "表格内容已保存在本机草稿中，待上传附件离开后将被清空，确认离开吗？",
        okText: "离开",
        cancelText: "继续填写",
        okButtonProps: { danger: true },
        onOk: () => {
          flushBatchDraftSave();
          onLeave();
        },
        onCancel: onStay,
      }),
    [flushBatchDraftSave, modal],
  );

  useEffect(() => {
    if (!batchWorkspaceOpen) return;
    const preventUnload = (event: BeforeUnloadEvent) => {
      flushBatchDraftSave();
      event.preventDefault();
      event.returnValue = "";
    };
    const flushOnPageHide = () => flushBatchDraftSave();
    const flushWhenHidden = () => {
      if (document.visibilityState === "hidden") flushBatchDraftSave();
    };
    window.addEventListener("beforeunload", preventUnload);
    window.addEventListener("pagehide", flushOnPageHide);
    document.addEventListener("visibilitychange", flushWhenHidden);
    return () => {
      window.removeEventListener("beforeunload", preventUnload);
      window.removeEventListener("pagehide", flushOnPageHide);
      document.removeEventListener("visibilitychange", flushWhenHidden);
    };
  }, [batchWorkspaceOpen, flushBatchDraftSave]);

  useEffect(() => {
    if (navigationBlocker.state !== "blocked") return;
    const confirmation = confirmBatchWorkspaceLeave(
      () => navigationBlocker.proceed(),
      () => navigationBlocker.reset(),
    );
    return () => confirmation.destroy();
  }, [confirmBatchWorkspaceLeave, navigationBlocker]);

  const metadataQuery = useQuery({
    queryKey: ["project-metadata"],
    queryFn: ({ signal }) => fetchProjectMetadata(signal),
    staleTime: 30 * 60_000,
  });
  const listQuery = useQuery({
    queryKey: [
      "projects",
      page,
      search,
      statusFilter,
      workTypeFilter,
      ownerUserId,
      reviewAssignmentStatus,
      submittedDate,
      recycleBin,
    ],
    queryFn: ({ signal }) =>
      fetchProjects(
        {
          page,
          pageSize: 10,
          search,
          status: statusFilter,
          workTypeCode: workTypeFilter,
          ownerUserId,
          reviewAssignmentStatus,
          submittedDate,
          deleted: isAdmin && recycleBin,
        },
        signal,
      ),
  });
  const registrantsQuery = useQuery({
    queryKey: ["project-registrant-options"],
    queryFn: ({ signal }) => fetchRegistrants({ page: 1, pageSize: 100 }, signal),
    enabled: !isAdmin,
    staleTime: 30_000,
  });
  const deferredBatchPartySearch = useDeferredValue(batchPartySearch.trim());
  const batchPartyRegistrantsQuery = useQuery({
    queryKey: [
      "batch-project-registrants",
      batchPartyPage,
      batchPartyPageSize,
      deferredBatchPartySearch,
    ],
    queryFn: ({ signal }) =>
      fetchRegistrants(
        {
          page: batchPartyPage,
          pageSize: batchPartyPageSize,
          search: deferredBatchPartySearch || undefined,
        },
        signal,
      ),
    enabled: Boolean(!isAdmin && batchPartyPicker),
    staleTime: 30_000,
  });
  const ownersQuery = useQuery({
    queryKey: ["project-owner-options"],
    queryFn: ({ signal }) => fetchUsers({ page: 1, pageSize: 100, role: "NORMAL_USER" }, signal),
    enabled: isAdmin,
    staleTime: 60_000,
  });
  const allCountQuery = useQuery({
    queryKey: [
      "project-count",
      "all",
      search,
      workTypeFilter,
      ownerUserId,
      reviewAssignmentStatus,
      submittedDate,
      recycleBin,
    ],
    queryFn: ({ signal }) =>
      fetchProjects(
        {
          page: 1,
          pageSize: 10,
          search,
          workTypeCode: workTypeFilter,
          ownerUserId,
          reviewAssignmentStatus,
          submittedDate,
          deleted: isAdmin && recycleBin,
        },
        signal,
      ),
    enabled: statusFilter !== undefined,
  });
  const submittedCountQuery = useQuery({
    queryKey: [
      "project-count",
      "submitted",
      search,
      workTypeFilter,
      ownerUserId,
      reviewAssignmentStatus,
      submittedDate,
      recycleBin,
    ],
    queryFn: ({ signal }) =>
      fetchProjects(
        {
          page: 1,
          pageSize: 10,
          search,
          status: "SUBMITTED",
          workTypeCode: workTypeFilter,
          ownerUserId,
          reviewAssignmentStatus,
          submittedDate,
          deleted: isAdmin && recycleBin,
        },
        signal,
      ),
    enabled: statusFilter !== "SUBMITTED",
  });
  const completedCountQuery = useQuery({
    queryKey: [
      "project-count",
      "completed",
      search,
      workTypeFilter,
      ownerUserId,
      reviewAssignmentStatus,
      submittedDate,
      recycleBin,
    ],
    queryFn: ({ signal }) =>
      fetchProjects(
        {
          page: 1,
          pageSize: 10,
          search,
          status: "COMPLETED",
          workTypeCode: workTypeFilter,
          ownerUserId,
          reviewAssignmentStatus,
          submittedDate,
          deleted: isAdmin && recycleBin,
        },
        signal,
      ),
    enabled: statusFilter !== "COMPLETED",
  });
  const reviewersQuery = useQuery({
    queryKey: ["project-assignment-reviewers"],
    queryFn: () => fetchReviewerOptions(),
    enabled: Boolean(isAdmin && assignmentOpen),
    staleTime: 30_000,
  });

  const workTypeLabels = useMemo(
    () =>
      Object.fromEntries(
        metadataQuery.data?.work_types.map((item) => [item.code, item.label]) ?? [],
      ),
    [metadataQuery.data],
  );
  const registrantById = useMemo(
    () =>
      new Map(
        [...(registrantsQuery.data?.items ?? []), ...Object.values(batchPartyRegistrantCache)].map(
          (item) => [item.id, item],
        ),
      ),
    [batchPartyRegistrantCache, registrantsQuery.data?.items],
  );
  const partyNameById = useMemo(
    () => new Map(Array.from(registrantById, ([id, registrant]) => [id, registrant.name] as const)),
    [registrantById],
  );
  useEffect(() => {
    batchRowsRef.current = batchRows;
  }, [batchRows]);

  useEffect(() => {
    if (!batchDraftSaveInitializedRef.current) {
      batchDraftSaveInitializedRef.current = true;
      return;
    }

    setBatchDraftSaveStatus("saving");
    if (batchDraftSavePriorityRef.current === "immediate") {
      flushBatchDraftSave();
      return;
    }

    if (batchDraftDebounceTimerRef.current !== undefined) {
      window.clearTimeout(batchDraftDebounceTimerRef.current);
    }
    batchDraftDebounceTimerRef.current = window.setTimeout(
      flushBatchDraftSave,
      BATCH_DRAFT_SAVE_DEBOUNCE_MS,
    );
    if (batchDraftMaxWaitTimerRef.current === undefined) {
      batchDraftMaxWaitTimerRef.current = window.setTimeout(
        flushBatchDraftSave,
        BATCH_DRAFT_SAVE_MAX_WAIT_MS,
      );
    }
  }, [batchRows, flushBatchDraftSave]);

  useEffect(() => () => clearBatchDraftSaveTimers(), [clearBatchDraftSaveTimers]);

  const saveMutation = useMutation({
    mutationFn: (values: ProjectFormValues) => createProject(toProjectPayload(values)),
    onSuccess: async (saved) => {
      message.success(`项目 ${saved.project_number} 已提交`);
      setFormOpen(false);
      form.resetFields();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["projects"] }),
        queryClient.invalidateQueries({ queryKey: ["project-count"] }),
      ]);
      await navigate(`/projects/${saved.id}`);
    },
    onError: (error) => message.error(errorMessage(error, "项目保存失败")),
  });
  const validateBasicImportMutation = useMutation({
    mutationFn: validateBasicProjectImport,
    onSuccess: (result) => {
      setBasicImportResult(result);
      if (result.failed_rows > 0) {
        setBasicImportErrorsOpen(true);
        message.error(`Excel 有 ${result.failed_rows} 行未通过校验，请修正后重新导入`);
        return;
      }

      const importedRows = result.rows.map<ProjectBatchRow>((row) => ({
        ...createEmptyBatchRow(),
        work_name: row.work_name,
        work_type_code: row.work_type_code,
        rights_mode_code: row.rights_mode_code,
        completion_date: row.completion_date,
        publication_date: row.publication_date ?? "",
        cloud_drive_info: row.cloud_drive_info ?? "",
        work_summary: row.work_summary,
        creation_process: row.creation_process,
      }));
      requestImmediateBatchDraftSave();
      setBatchRows((current) => {
        const retainedRows = current.filter(
          (row) => hasBatchRowData(row) || (batchAttachmentFiles[row.id]?.length ?? 0) > 0,
        );
        return [...retainedRows, ...importedRows];
      });
      setSelectedBatchRowIds([]);
      setBatchFile(undefined);
      message.success(`已导入 ${result.valid_rows} 行基础信息，请继续补充著作权人、作者及附件`);
    },
    onError: (error) => message.error(errorMessage(error, "Excel 校验失败")),
  });
  const batchSubmitMutation = useMutation({
    mutationFn: async (rows: ProjectBatchRow[]) => {
      const pendingRows = rows.filter((row) =>
        [
          row.work_name,
          row.work_type_code,
          row.rights_mode_code,
          row.completion_date,
          row.publication_date,
          row.agent_name,
          row.contact_phone,
          row.cloud_drive_info,
          row.work_summary,
          row.creation_process,
          ...(row.author_ids ?? []),
          ...(row.right_holder_ids ?? []),
          ...(batchAttachmentFiles[row.id] ?? []),
        ].some(Boolean),
      );
      if (!pendingRows.length) throw new Error("请至少填写一行项目资料");
      const invalidIndex = pendingRows.findIndex((row) => !isBatchRowSubmittable(row));
      if (invalidIndex >= 0) {
        throw new Error(`第 ${invalidIndex + 1} 行信息不完整、字数超限或人数不符合要求`);
      }
      const saved: Array<{ project: ProjectDetail; rowId: string }> = [];
      for (const row of pendingRows) {
        saved.push({ project: await createProject(toProjectPayload(row)), rowId: row.id });
      }
      const failedAttachments: string[] = [];
      for (const item of saved) {
        for (const staged of batchAttachmentFiles[item.rowId] ?? []) {
          try {
            const intent = await createUploadIntent({ projectId: item.project.id }, staged.file);
            await uploadToIntent(intent, staged.file, () => undefined);
            await completeUpload(intent.attachment.id);
          } catch {
            failedAttachments.push(staged.file.name);
          }
        }
      }
      return { saved, failedAttachments };
    },
    onSuccess: async ({ saved, failedAttachments }) => {
      message.success(`已批量提交 ${saved.length} 个项目`);
      if (failedAttachments.length) {
        message.warning(`${failedAttachments.length} 个附件上传失败，请在对应项目详情中重新上传`);
      }
      requestImmediateBatchDraftSave();
      setBatchRows(Array.from({ length: 6 }, () => createEmptyBatchRow()));
      setSelectedBatchRowIds([]);
      setBatchAttachmentFiles({});
      setBatchWorkspaceOpen(false);
      await refreshList();
    },
    onError: (error) => message.error(errorMessage(error, "批量提交失败")),
  });
  const assignmentMutation = useMutation({
    mutationFn: (reviewerIds: string[]) => createReviewAssignments(selectedProjectIds, reviewerIds),
    onSuccess: async (result) => {
      const duplicateText = result.already_active_count
        ? `，${result.already_active_count} 条重复关系已跳过`
        : "";
      message.success(`已创建 ${result.created.length} 条审核任务${duplicateText}`);
      setAssignmentOpen(false);
      setSelectedProjectIds([]);
      await queryClient.invalidateQueries({ queryKey: ["review-assignments"] });
    },
    onError: (error) => message.error(errorMessage(error, "审核任务分配失败")),
  });
  const projectBatchCompleteMutation = useMutation({
    mutationFn: (projects: ProjectListItem[]) =>
      batchUpdateProjectStatus(
        projects.map((project) => ({ id: project.id, version: project.version })),
        "COMPLETED",
      ),
    onSuccess: async (result) => {
      message.success(
        result.processed_count
          ? `已将 ${result.processed_count} 个项目标记为完成`
          : "选中的项目均已标记为完成",
      );
      setSelectedProjectIds([]);
      await refreshList();
    },
    onError: (error) => message.error(errorMessage(error, "批量标记完成失败")),
  });
  const projectBatchDeleteMutation = useMutation({
    mutationFn: batchDeleteProjects,
    onSuccess: async (result) => {
      const revokeText = result.revoked_assignment_count
        ? `，并撤销 ${result.revoked_assignment_count} 条审核任务`
        : "";
      message.success(`已删除 ${result.processed_count} 个项目${revokeText}`);
      setSelectedProjectIds([]);
      await Promise.all([
        refreshList(),
        queryClient.invalidateQueries({ queryKey: ["admin-review-tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["reviewer-options"] }),
      ]);
    },
    onError: (error) => message.error(errorMessage(error, "批量删除失败")),
  });

  const openCreate = () => {
    setChoiceOpen(true);
  };
  const openSingleCreate = () => {
    setChoiceOpen(false);
    form.resetFields();
    form.setFieldsValue({ author_ids: [], right_holder_ids: [] });
    setFormOpen(true);
  };
  const openBatchChoice = () => {
    setChoiceOpen(false);
    openBatchWorkspace();
  };
  const openBatchWorkspace = () => {
    setBatchChoiceOpen(false);
    requestImmediateBatchDraftSave();
    setBatchRows((current) =>
      current.map((row) => ({ ...row, agent_name: "", contact_phone: "" })),
    );
    setBatchWorkspaceOpen(true);
  };
  const updateBatchRow = <K extends keyof ProjectBatchRow>(
    rowId: string,
    field: K,
    value: ProjectBatchRow[K],
    saveImmediately = false,
  ) => {
    if (saveImmediately) requestImmediateBatchDraftSave();
    setBatchRows((current) =>
      current.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)),
    );
  };
  const openSinglePartyPicker = (field: BatchPartyField) => {
    const rightsModeCode = form.getFieldValue("rights_mode_code") as string | undefined;
    const selectedIds = (form.getFieldValue(field) as string[] | undefined) ?? [];
    const maxCount = getProjectPartyLimit(rightsModeCode ?? "");
    setBatchPartyPicker({
      source: "single",
      field,
      title: field === "right_holder_ids" ? "著作权人" : "作者",
      maxCount,
    });
    setBatchPartyDraftIds(selectedIds.slice(0, maxCount));
    setBatchPartySearch("");
    setBatchPartyPage(1);
  };
  const updateSingleRightsMode = (value: string) => {
    const partyLimit = getProjectPartyLimit(value);
    const authorIds = (form.getFieldValue("author_ids") as string[] | undefined) ?? [];
    const rightHolderIds = (form.getFieldValue("right_holder_ids") as string[] | undefined) ?? [];
    const trimmed = authorIds.length > partyLimit || rightHolderIds.length > partyLimit;
    form.setFieldsValue({
      author_ids: authorIds.slice(0, partyLimit),
      right_holder_ids: rightHolderIds.slice(0, partyLimit),
    });
    if (trimmed) message.info("非合作作品仅允许一位著作权人和一位作者，已保留首位");
  };
  const openBatchPartyPicker = (row: ProjectBatchRow, field: BatchPartyField) => {
    const maxCount = getProjectPartyLimit(row.rights_mode_code);
    setBatchPartyPicker({
      source: "batch",
      rowId: row.id,
      field,
      title: field === "right_holder_ids" ? "著作权人" : "作者",
      maxCount,
    });
    setBatchPartyDraftIds(row[field].slice(0, maxCount));
    setBatchPartySearch("");
    setBatchPartyPage(1);
  };
  const closeBatchPartyPicker = () => {
    setBatchPartyPicker(null);
    setBatchPartyDraftIds([]);
    setBatchPartySearch("");
    setBatchPartyPage(1);
  };
  const confirmBatchPartyPicker = () => {
    if (!batchPartyPicker) return;
    const { source, rowId, field, maxCount } = batchPartyPicker;
    const selectedIds = batchPartyDraftIds.slice(0, maxCount);
    if (source === "single") {
      form.setFieldValue(field, selectedIds);
    } else if (rowId) {
      requestImmediateBatchDraftSave();
      setBatchRows((current) =>
        current.map((row) => (row.id === rowId ? { ...row, [field]: selectedIds } : row)),
      );
    }
    closeBatchPartyPicker();
  };
  const updateBatchRightsMode = (row: ProjectBatchRow, value: string) => {
    const partyLimit = getProjectPartyLimit(value);
    const trimmed = row.author_ids.length > partyLimit || row.right_holder_ids.length > partyLimit;
    requestImmediateBatchDraftSave();
    setBatchRows((current) =>
      current.map((item) =>
        item.id === row.id
          ? {
              ...item,
              rights_mode_code: value,
              author_ids: item.author_ids.slice(0, partyLimit),
              right_holder_ids: item.right_holder_ids.slice(0, partyLimit),
            }
          : item,
      ),
    );
    if (trimmed) message.info("非合作作品仅允许一位著作权人和一位作者，已保留首位");
  };
  const syncBatchColumn = (field: BatchSyncField, label: string) => {
    const firstRow = batchRows[0];
    if (!firstRow) return;
    const sourceValue = firstRow[field];
    const shouldTrimParties =
      field === "rights_mode_code" && Boolean(sourceValue) && sourceValue !== "COOPERATIVE_WORK";
    const trimmed =
      shouldTrimParties &&
      batchRows.some((row) => row.author_ids.length > 1 || row.right_holder_ids.length > 1);
    requestImmediateBatchDraftSave();
    setBatchRows((current) =>
      current.map((row) => ({
        ...row,
        [field]: Array.isArray(sourceValue) ? [...sourceValue] : sourceValue,
        ...(shouldTrimParties
          ? {
              author_ids: row.author_ids.slice(0, 1),
              right_holder_ids: row.right_holder_ids.slice(0, 1),
            }
          : {}),
      })),
    );
    const isEmpty = Array.isArray(sourceValue) ? sourceValue.length === 0 : !sourceValue;
    message.success(isEmpty ? `${label}列已清空` : `${label}列已按第一行同步`);
    if (trimmed) message.info("非合作作品仅允许一位著作权人和一位作者，已保留首位");
  };
  const renderBatchColumnTitle = (
    label: string,
    field: BatchSyncField,
    options?: { required?: boolean; hint?: string },
  ) => (
    <span className="batch-column-title">
      <span>
        {label}
        {options?.required ? <span className="batch-required-mark">*</span> : null}
      </span>
      <Tooltip title={`以第一行的${label}同步整列`}>
        <Button
          className="batch-column-sync-button"
          type="text"
          size="small"
          icon={<SyncOutlined />}
          aria-label={`同步${label}`}
          onClick={(event) => {
            event.stopPropagation();
            syncBatchColumn(field, label);
          }}
        />
      </Tooltip>
      {options?.hint ? (
        <Tooltip title={options.hint}>
          <Button
            className="batch-column-help-button"
            type="text"
            size="small"
            icon={<InfoCircleOutlined />}
            aria-label={`${label}说明`}
            onClick={(event) => event.stopPropagation()}
          />
        </Tooltip>
      ) : null}
    </span>
  );
  const renderBatchInlineTextArea = (
    row: ProjectBatchRow,
    field: BatchInlineTextField,
    options: { limit: number; placeholder: string; required?: boolean },
  ) => {
    const value = row[field] ?? "";
    const hasError =
      Boolean(options.required && !value.trim() && hasBatchRowData(row)) || value.length > options.limit;
    return (
      <div className={`batch-inline-text-cell ${hasError ? "is-error" : ""}`}>
        <Input.TextArea
          value={value}
          rows={2}
          maxLength={options.limit}
          showCount
          placeholder={options.placeholder}
          onChange={(event) => updateBatchRow(row.id, field, event.target.value)}
          onBlur={() => requestImmediateBatchDraftSave()}
        />
      </div>
    );
  };
  const stageBatchAttachment = (rowId: string, file: File, selectedFiles: File[]) => {
    const extension = file.name.includes(".") ? file.name.split(".").pop()!.toLowerCase() : "";
    if (!BATCH_ATTACHMENT_EXTENSIONS.has(extension)) {
      message.error(`${file.name} 的文件格式不支持`);
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_BATCH_ATTACHMENT_SIZE) {
      message.error(`${file.name} 超过 100MB`);
      return Upload.LIST_IGNORE;
    }
    const currentCount = batchAttachmentFiles[rowId]?.length ?? 0;
    const selectedIndex = selectedFiles.indexOf(file);
    if (currentCount + selectedIndex >= MAX_BATCH_ATTACHMENTS) {
      if (currentCount + selectedIndex === MAX_BATCH_ATTACHMENTS) {
        message.warning(`每个项目最多添加 ${MAX_BATCH_ATTACHMENTS} 个附件`);
      }
      return Upload.LIST_IGNORE;
    }
    requestImmediateBatchDraftSave();
    setBatchAttachmentFiles((current) => ({
      ...current,
      [rowId]: [...(current[rowId] ?? []), { id: createRowId(), file }],
    }));
    return false;
  };
  const removeBatchAttachment = (rowId: string, attachmentId: string) => {
    requestImmediateBatchDraftSave();
    setBatchAttachmentFiles((current) => ({
      ...current,
      [rowId]: (current[rowId] ?? []).filter((item) => item.id !== attachmentId),
    }));
  };
  const renderBatchAttachmentCell = (row: ProjectBatchRow) => {
    const stagedAttachments = batchAttachmentFiles[row.id] ?? [];
    const canUploadMore = stagedAttachments.length < MAX_BATCH_ATTACHMENTS;
    const uploadControl = (
      <Upload
        multiple
        accept={BATCH_ATTACHMENT_ACCEPT}
        showUploadList={false}
        beforeUpload={(file, selectedFiles) => stageBatchAttachment(row.id, file, selectedFiles)}
        disabled={!canUploadMore}
      >
        <Tooltip title={canUploadMore ? "上传附件" : `最多支持 ${MAX_BATCH_ATTACHMENTS} 个附件`}>
          <Button
            className="batch-attachment-upload-button"
            size="small"
            icon={<UploadOutlined />}
            disabled={!canUploadMore}
            aria-label="上传附件"
          />
        </Tooltip>
      </Upload>
    );

    if (!stagedAttachments.length) {
      return (
        <div className="batch-attachment-cell">
          <Upload
            multiple
            accept={BATCH_ATTACHMENT_ACCEPT}
            showUploadList={false}
            beforeUpload={(file, selectedFiles) => stageBatchAttachment(row.id, file, selectedFiles)}
          >
            <Button className="batch-attachment-empty-button" size="small" icon={<UploadOutlined />}>
              上传
            </Button>
          </Upload>
        </div>
      );
    }

    const content = (
      <div className="batch-attachment-popover">
        <div className="batch-attachment-popover-head">
          已添加 {stagedAttachments.length}/{MAX_BATCH_ATTACHMENTS}
        </div>
        <div className="batch-attachment-list">
          {stagedAttachments.map((item) => (
            <div className="batch-attachment-item" key={item.id}>
              <span title={item.file.name}>{item.file.name}</span>
              <Button
                danger
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                aria-label={`删除${item.file.name}`}
                onClick={() => removeBatchAttachment(row.id, item.id)}
              />
            </div>
          ))}
        </div>
      </div>
    );

    return (
      <div className="batch-attachment-cell">
        <Popover content={content} trigger="click" placement="leftTop">
          <Button className="batch-attachment-count-button" size="small">
            {stagedAttachments.length}/{MAX_BATCH_ATTACHMENTS}
          </Button>
        </Popover>
        {uploadControl}
      </div>
    );
  };
  const renderBatchPartyCell = (row: ProjectBatchRow, field: BatchPartyField) => {
    const title = field === "right_holder_ids" ? "著作权人" : "作者";
    const selectedIds = row[field];
    if (!selectedIds.length) {
      return (
        <Button
          className="batch-party-add-button"
          icon={<PlusOutlined />}
          onClick={() => openBatchPartyPicker(row, field)}
        >
          添加{title}
        </Button>
      );
    }
    return (
      <Tooltip title={`点击修改${title}`}>
        <button
          className="batch-party-selection"
          type="button"
          onClick={() => openBatchPartyPicker(row, field)}
        >
          {selectedIds.map((id) => {
            const name = registrantById.get(id)?.name ?? "登记人已失效";
            return (
              <span key={id} title={name}>
                {name}
              </span>
            );
          })}
        </button>
      </Tooltip>
    );
  };
  const renderPartyPickerModal = () => (
    <Modal
      className="batch-party-picker-modal"
      title={`选择${batchPartyPicker?.title ?? "登记人"}`}
      open={Boolean(batchPartyPicker)}
      width={720}
      zIndex={1200}
      okText="确认选择"
      cancelText="取消"
      onOk={confirmBatchPartyPicker}
      onCancel={closeBatchPartyPicker}
    >
      <div className="batch-party-picker-toolbar">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          value={batchPartySearch}
          placeholder="搜索登记人名称、编号或证件号码"
          onChange={(event) => {
            setBatchPartySearch(event.target.value);
            setBatchPartyPage(1);
          }}
        />
        <span>
          已选择 {batchPartyDraftIds.length}/{batchPartyPicker?.maxCount ?? 1}
        </span>
      </div>
      <Table<Registrant>
        className="batch-party-picker-table"
        rowKey="id"
        size="middle"
        loading={batchPartyRegistrantsQuery.isPending || batchPartyRegistrantsQuery.isFetching}
        dataSource={batchPartyRegistrantsQuery.data?.items ?? []}
        pagination={{
          current: batchPartyPage,
          pageSize: batchPartyPageSize,
          total: batchPartyRegistrantsQuery.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50],
          showTotal: (total) => `共 ${total} 个登记人`,
          onChange: (nextPage, nextPageSize) => {
            setBatchPartyPageSize(nextPageSize);
            setBatchPartyPage(nextPageSize === batchPartyPageSize ? nextPage : 1);
          },
        }}
        scroll={{ y: 320 }}
        locale={{ emptyText: "暂无可选择的登记人" }}
        rowSelection={{
          type: batchPartyPicker?.maxCount === 1 ? "radio" : "checkbox",
          selectedRowKeys: batchPartyDraftIds,
          preserveSelectedRowKeys: true,
          onChange: (keys, selectedRows) => {
            const nextIds = keys.map(String);
            const maxCount = batchPartyPicker?.maxCount ?? 1;
            if (nextIds.length > maxCount) {
              message.warning(`当前权利归属方式最多选择 ${maxCount} 个登记人`);
              return;
            }
            setBatchPartyRegistrantCache((current) => ({
              ...current,
              ...Object.fromEntries(selectedRows.map((record) => [record.id, record])),
            }));
            setBatchPartyDraftIds(nextIds);
          },
          getCheckboxProps: (record) => ({
            disabled:
              (batchPartyPicker?.maxCount ?? 1) > 1 &&
              batchPartyDraftIds.length >= (batchPartyPicker?.maxCount ?? 1) &&
              !batchPartyDraftIds.includes(record.id),
          }),
        }}
        columns={[
          {
            title: "登记人名称",
            dataIndex: "name",
            ellipsis: true,
          },
          {
            title: "登记人类别",
            dataIndex: "registrant_type",
            width: 160,
            render: (value: Registrant["registrant_type"]) =>
              value === "NATURAL_PERSON" ? "自然人" : "法人或其他组织",
          },
          {
            title: "证件号码",
            dataIndex: "certificate_number_masked",
            width: 190,
          },
        ]}
      />
    </Modal>
  );
  const addBatchRow = () => {
    requestImmediateBatchDraftSave();
    setBatchRows((current) => [...current, createEmptyBatchRow()]);
  };
  const copyBatchRows = () => {
    const sourceRows = batchRows.filter((row) => selectedBatchRowIds.includes(row.id));
    if (!sourceRows.length) {
      message.warning("请先选择需要复制的行");
      return;
    }
    const copiedRows = sourceRows.map((row) => ({ ...row, id: createRowId() }));
    requestImmediateBatchDraftSave();
    setBatchRows((current) => [...current, ...copiedRows]);
    setBatchAttachmentFiles((current) => {
      const next = { ...current };
      sourceRows.forEach((row, index) => {
        next[copiedRows[index].id] = (current[row.id] ?? []).map((item) => ({
          ...item,
          id: createRowId(),
        }));
      });
      return next;
    });
    setSelectedBatchRowIds([]);
  };
  const removeBatchRows = () => {
    if (!selectedBatchRowIds.length) {
      message.warning("请先选择需要删除的行");
      return;
    }
    requestImmediateBatchDraftSave();
    setBatchRows((current) => {
      const nextRows = current.filter((row) => !selectedBatchRowIds.includes(row.id));
      return nextRows.length ? nextRows : [createEmptyBatchRow()];
    });
    setBatchAttachmentFiles((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([rowId]) => !selectedBatchRowIds.includes(rowId)),
      ),
    );
    setSelectedBatchRowIds([]);
  };
  const removeBatchRow = (rowId: string) => {
    requestImmediateBatchDraftSave();
    setBatchRows((current) => {
      const nextRows = current.filter((row) => row.id !== rowId);
      return nextRows.length ? nextRows : [createEmptyBatchRow()];
    });
    setBatchAttachmentFiles((current) =>
      Object.fromEntries(Object.entries(current).filter(([id]) => id !== rowId)),
    );
    setSelectedBatchRowIds((current) => current.filter((id) => id !== rowId));
  };
  const downloadTemplate = async () => {
    try {
      saveBlob(
        await downloadBasicProjectImportTemplate(),
        "浙江视听版权工作站版权登记资料清单模版.xlsx",
      );
    } catch (error) {
      message.error(errorMessage(error, "模板下载失败"));
    }
  };
  const saveBatchDraft = () => {
    if (flushBatchDraftSave(true)) {
      message.success("表格内容已保存，待上传附件仅在当前页面保留");
      return;
    }
    message.error("草稿保存失败，请检查浏览器存储空间后重试");
  };
  const matchMetadataCode = (
    items: Array<{ code: string; label: string }> | undefined,
    value = "",
  ) => {
    const normalized = value.trim();
    if (!normalized) return "";
    return (
      items?.find((item) => item.code === normalized || item.label === normalized)?.code ??
      normalized
    );
  };
  const matchRegistrantIds = (value = "") => {
    const parts = value
      .split(/[;；,，、\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
    if (!parts.length) return [];
    const registrants = registrantsQuery.data?.items ?? [];
    return parts
      .map(
        (part) =>
          registrants.find(
            (registrant) =>
              registrant.id === part ||
              registrant.public_id === part ||
              registrant.name === part ||
              `${registrant.name} · ${registrant.public_id}` === part,
          )?.id,
      )
      .filter((id): id is string => Boolean(id));
  };
  const pasteBatchRows = (startRowId: string, event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData("text");
    if (!text.includes("\t") && !text.includes("\n")) return;
    event.preventDefault();

    const startIndex = batchRows.findIndex((row) => row.id === startRowId);
    const pastedRows = text
      .trim()
      .split(/\r?\n/)
      .map((line) => line.split("\t"));
    requestImmediateBatchDraftSave();
    setBatchRows((current) => {
      const nextRows = [...current];
      pastedRows.forEach((cells, offset) => {
        const rowIndex = Math.max(startIndex, 0) + offset;
        while (nextRows.length <= rowIndex) nextRows.push(createEmptyBatchRow());
        const currentRow = nextRows[rowIndex];
        const rightsModeCode =
          matchMetadataCode(metadataQuery.data?.rights_modes, cells[2]) ||
          currentRow.rights_mode_code;
        const partyLimit = getProjectPartyLimit(rightsModeCode);
        const rightHolderIds = cells[5]
          ? matchRegistrantIds(cells[5])
          : currentRow.right_holder_ids;
        const authorIds = cells[6] ? matchRegistrantIds(cells[6]) : currentRow.author_ids;
        nextRows[rowIndex] = {
          ...currentRow,
          work_name: cells[0]?.trim() ?? currentRow.work_name,
          work_type_code:
            normalizeWorkTypeCode(matchMetadataCode(metadataQuery.data?.work_types, cells[1])) ||
            currentRow.work_type_code,
          rights_mode_code: rightsModeCode,
          completion_date: cells[3]?.trim() ?? currentRow.completion_date,
          publication_date: cells[4]?.trim() ?? currentRow.publication_date,
          right_holder_ids: rightHolderIds.slice(0, partyLimit),
          author_ids: authorIds.slice(0, partyLimit),
          agent_name: "",
          contact_phone: "",
          work_summary: cells[7]?.trim() ?? currentRow.work_summary,
          creation_process: cells[8]?.trim() ?? currentRow.creation_process,
          cloud_drive_info: cells[9]?.trim() ?? currentRow.cloud_drive_info,
        };
      });
      return nextRows;
    });
  };
  const projectDetailPath = (record: ProjectListItem) =>
    `${isAdmin ? "/admin/projects" : "/projects"}/${record.id}`;
  const refreshList = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["projects"] }),
      queryClient.invalidateQueries({ queryKey: ["project-count"] }),
    ]);
  };
  const changeStatus = async (record: ProjectListItem, nextStatus: ProjectStatus) => {
    try {
      await updateProjectStatus(record.id, nextStatus, record.version);
      message.success(`项目已标记为${STATUS_LABELS[nextStatus]}`);
      await refreshList();
    } catch (error) {
      message.error(errorMessage(error, "标签更新失败"));
    }
  };
  const remove = async (record: ProjectListItem) => {
    try {
      await deleteProject(record.id);
      message.success("项目已移入回收站");
      await refreshList();
    } catch (error) {
      message.error(errorMessage(error, "项目删除失败"));
    }
  };
  const restore = async (record: ProjectListItem) => {
    try {
      await restoreProject(record.id);
      message.success("项目已恢复，原标签保持不变");
      await refreshList();
    } catch (error) {
      message.error(errorMessage(error, "项目恢复失败"));
    }
  };
  const confirmRemove = (record: ProjectListItem) => {
    modal.confirm({
      title: `删除“${record.work_name}”？`,
      content: "项目将移入回收站，管理员之后仍可恢复。",
      okText: "确认删除",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: () => remove(record),
    });
  };
  const selectedProjectRecords = (listQuery.data?.items ?? []).filter((project) =>
    selectedProjectIds.includes(project.id),
  );
  const confirmBatchComplete = () => {
    if (!selectedProjectRecords.length) return;
    modal.confirm({
      title: `确认批量标记 ${selectedProjectRecords.length} 个项目为完成？`,
      content: "已完成的项目将保持不变，其余项目会统一更新为已完成。",
      okText: "确认标记完成",
      cancelText: "取消",
      onOk: () => projectBatchCompleteMutation.mutateAsync(selectedProjectRecords),
    });
  };
  const confirmBatchDelete = () => {
    if (!selectedProjectIds.length) return;
    modal.confirm({
      title: `确认批量删除 ${selectedProjectIds.length} 个项目？`,
      content: "项目将移入回收站，相关审核任务会立即撤销，管理员之后仍可恢复项目。",
      okText: "确认批量删除",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: () => projectBatchDeleteMutation.mutateAsync(selectedProjectIds),
    });
  };
  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatusFilter(undefined);
    setWorkTypeFilter(undefined);
    setOwnerUserId(undefined);
    setReviewAssignmentStatus(undefined);
    setSubmittedDate("");
    setPage(1);
    setSelectedProjectIds([]);
  };
  const selectProject = (projectId: string, selected: boolean) => {
    setSelectedProjectIds((current) =>
      selected ? [...new Set([...current, projectId])] : current.filter((id) => id !== projectId),
    );
  };
  const visibleProjectIds = (listQuery.data?.items ?? []).map((project) => project.id);
  const toggleCurrentPage = (checked: boolean) => {
    setSelectedProjectIds((current) => {
      if (!checked) return current.filter((id) => !visibleProjectIds.includes(id));
      return [...new Set([...current, ...visibleProjectIds])];
    });
  };

  const batchColumns = [
    {
      title: "序号",
      width: 58,
      fixed: "left" as const,
      render: (_: unknown, __: ProjectBatchRow, index: number) => index + 1,
    },
    {
      title: (
        <span>
          作品名称<span className="batch-required-mark">*</span>
        </span>
      ),
      dataIndex: "work_name",
      width: 170,
      fixed: "left" as const,
      render: (_: string, row: ProjectBatchRow) => (
        <Input
          value={row.work_name}
          placeholder="请输入作品名称"
          onChange={(event) => updateBatchRow(row.id, "work_name", event.target.value)}
          onPaste={(event) => pasteBatchRows(row.id, event)}
        />
      ),
    },
    {
      title: renderBatchColumnTitle("作品类型", "work_type_code", { required: true }),
      dataIndex: "work_type_code",
      width: 130,
      render: (_: string, row: ProjectBatchRow) => (
        <Select
          value={row.work_type_code || undefined}
          placeholder="请选择"
          options={metadataQuery.data?.work_types.map((item) => ({
            value: item.code,
            label: item.label,
          }))}
          onChange={(value) => updateBatchRow(row.id, "work_type_code", value, true)}
        />
      ),
    },
    {
      title: renderBatchColumnTitle("权利归属方式", "rights_mode_code", { required: true }),
      dataIndex: "rights_mode_code",
      width: 145,
      render: (_: string, row: ProjectBatchRow) => (
        <Select
          value={row.rights_mode_code || undefined}
          placeholder="请选择"
          options={metadataQuery.data?.rights_modes.map((item) => ({
            value: item.code,
            label: item.label,
          }))}
          onChange={(value) => updateBatchRightsMode(row, value)}
        />
      ),
    },
    {
      title: renderBatchColumnTitle("创作完成时间", "completion_date", { required: true }),
      dataIndex: "completion_date",
      width: 145,
      render: (_: string, row: ProjectBatchRow) => (
        <DatePicker
          allowClear
          maxDate={dayjs()}
          value={row.completion_date ? dayjs(row.completion_date) : null}
          format="YYYY-MM-DD"
          placeholder="请选择"
          onChange={(value) =>
            updateBatchRow(row.id, "completion_date", value?.format("YYYY-MM-DD") ?? "", true)
          }
        />
      ),
    },
    {
      title: renderBatchColumnTitle("创作发表时间", "publication_date", {
        hint: "不填写表示未发表",
      }),
      dataIndex: "publication_date",
      width: 145,
      render: (_: string, row: ProjectBatchRow) => (
        <DatePicker
          allowClear
          maxDate={dayjs()}
          minDate={row.completion_date ? dayjs(row.completion_date) : undefined}
          value={row.publication_date ? dayjs(row.publication_date) : null}
          format="YYYY-MM-DD"
          placeholder="不填表示未发表"
          onChange={(value) =>
            updateBatchRow(row.id, "publication_date", value?.format("YYYY-MM-DD") ?? "", true)
          }
        />
      ),
    },
    {
      title: renderBatchColumnTitle("著作权人", "right_holder_ids", { required: true }),
      dataIndex: "right_holder_ids",
      width: 150,
      render: (_: string[], row: ProjectBatchRow) => renderBatchPartyCell(row, "right_holder_ids"),
    },
    {
      title: renderBatchColumnTitle("作者", "author_ids", { required: true }),
      dataIndex: "author_ids",
      width: 150,
      render: (_: string[], row: ProjectBatchRow) => renderBatchPartyCell(row, "author_ids"),
    },
    {
      title: (
        <span>
          作品简介<span className="batch-required-mark">*</span>
        </span>
      ),
      dataIndex: "work_summary",
      width: 220,
      render: (_: string, row: ProjectBatchRow) =>
        renderBatchInlineTextArea(row, "work_summary", {
          limit: PROJECT_LONG_TEXT_LIMIT,
          placeholder: "主要内容、主题、表现形式和创作意图，建议约 200 字",
          required: true,
        }),
    },
    {
      title: (
        <span>
          创作过程<span className="batch-required-mark">*</span>
        </span>
      ),
      dataIndex: "creation_process",
      width: 220,
      render: (_: string, row: ProjectBatchRow) =>
        renderBatchInlineTextArea(row, "creation_process", {
          limit: PROJECT_LONG_TEXT_LIMIT,
          placeholder: "创作起因、构思过程、主要工具或方法，建议约 200 字",
          required: true,
        }),
    },
    {
      title: "网盘信息",
      dataIndex: "cloud_drive_info",
      width: 210,
      render: (_: string, row: ProjectBatchRow) =>
        renderBatchInlineTextArea(row, "cloud_drive_info", {
          limit: PROJECT_CLOUD_TEXT_LIMIT,
          placeholder: "分享链接、提取码或访问说明",
        }),
    },
    {
      title: "样本和附件",
      key: "attachments",
      width: 132,
      render: (_: unknown, row: ProjectBatchRow) => renderBatchAttachmentCell(row),
    },
    {
      title: "操作",
      fixed: "right" as const,
      width: 76,
      render: (_: unknown, row: ProjectBatchRow) => {
        if (!hasBatchRowData(row) && !(batchAttachmentFiles[row.id]?.length ?? 0)) {
          return (
            <Button danger type="link" onClick={() => removeBatchRow(row.id)}>
              删除
            </Button>
          );
        }
        return (
          <Popconfirm
            title="确认删除该行？"
            description="该行已填写的数据将被删除，且无法恢复。"
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            onConfirm={() => removeBatchRow(row.id)}
          >
            <Button danger type="link">
              删除
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const batchDraftStatusText =
    batchDraftSaveStatus === "saving"
      ? "正在保存..."
      : batchDraftSaveStatus === "error"
        ? "保存失败，请重试"
        : batchDraftSavedAt
          ? `已保存 ${batchDraftSavedAt}`
          : "草稿已保存";
  const batchDraftStatusIcon =
    batchDraftSaveStatus === "saving" ? (
      <SyncOutlined spin />
    ) : batchDraftSaveStatus === "error" ? (
      <InfoCircleOutlined />
    ) : (
      <CloudUploadOutlined />
    );

  if (batchWorkspaceOpen) {
    return (
      <main className="management-page batch-registration-page">
        <div className="batch-registration-heading">
          <Button
            className="batch-back-button"
            type="link"
            icon={<LeftOutlined />}
            onClick={() => {
              void confirmBatchWorkspaceLeave(() => setBatchWorkspaceOpen(false));
            }}
          >
            返回项目列表
          </Button>
          <div>
            <Typography.Title level={1}>批量创建项目</Typography.Title>
            <Typography.Paragraph type="secondary">
              在线录入多个作品信息，支持新增、复制行、Excel 粘贴与批量提交
            </Typography.Paragraph>
          </div>
          <span className={`batch-save-indicator is-${batchDraftSaveStatus}`}>
            {batchDraftStatusIcon} {batchDraftStatusText}
          </span>
        </div>

        <section className="batch-table-panel">
          <header className="batch-table-header">
            <div>
              <strong>项目信息</strong>
              <span>共 {batchRows.length} 个项目</span>
            </div>
            <div className="batch-file-actions">
              <Button icon={<DownloadOutlined />} onClick={() => void downloadTemplate()}>
                下载模板
              </Button>
              <Upload
                accept=".xlsx"
                maxCount={1}
                showUploadList={false}
                beforeUpload={(file) => {
                  setBatchFile(file);
                  setBasicImportResult(undefined);
                  setBasicImportErrorsOpen(false);
                  return false;
                }}
              >
                <Button icon={<FileExcelOutlined />}>导入 Excel</Button>
              </Upload>
            </div>
          </header>

          <div className="batch-toolbar">
            <Input.Search placeholder="搜索作品名称" allowClear />
            <Button
              className="batch-add-row-button"
              type="default"
              icon={<PlusOutlined />}
              onClick={addBatchRow}
            >
              新增一行
            </Button>
            <Button icon={<CopyOutlined />} onClick={copyBatchRows}>
              复制行
            </Button>
            <Button icon={<DeleteOutlined />} onClick={removeBatchRows}>
              删除
            </Button>
            <span>在作品名称单元格可直接粘贴 Excel 多行数据</span>
          </div>

          {batchFile ? (
            <div className="batch-import-strip">
              <span>
                已选择 <strong>{batchFile.name}</strong>
              </span>
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                loading={validateBasicImportMutation.isPending}
                onClick={() => validateBasicImportMutation.mutate(batchFile)}
              >
                校验并导入
              </Button>
            </div>
          ) : null}

          {basicImportResult ? (
            <div
              className={`batch-import-result ${basicImportResult.failed_rows ? "is-error" : ""}`}
            >
              {basicImportResult.failed_rows ? <InfoCircleOutlined /> : <CheckCircleOutlined />}
              <span>
                {basicImportResult.failed_rows
                  ? `校验未通过：${basicImportResult.failed_rows} 行需要修正`
                  : `已导入 ${basicImportResult.valid_rows} 行基础信息`}
              </span>
              {basicImportResult.failed_rows ? (
                <Button onClick={() => setBasicImportErrorsOpen(true)}>查看错误</Button>
              ) : null}
            </div>
          ) : null}

          <Table<ProjectBatchRow>
            className="batch-registration-table"
            rowKey="id"
            dataSource={batchRows}
            columns={batchColumns}
            pagination={false}
            scroll={{ x: 2010 }}
            rowSelection={{
              fixed: true,
              selectedRowKeys: selectedBatchRowIds,
              onChange: (keys) => setSelectedBatchRowIds(keys.map(String)),
            }}
          />
          <p className="batch-table-note">
            网盘信息可填写分享链接或提取码，批量提交前将校验所有必填信息。
          </p>
        </section>

        <footer className="batch-submit-bar">
          <span className={`batch-footer-save-status is-${batchDraftSaveStatus}`}>
            {batchDraftStatusIcon} {batchDraftStatusText}
          </span>
          <strong>当前共 {batchRows.length} 个项目</strong>
          <em>批量提交前将校验所有必填信息</em>
          <div>
            <Button size="large" onClick={saveBatchDraft}>
              保存草稿
            </Button>
            <Button
              className="batch-primary-submit"
              type="primary"
              size="large"
              icon={<CheckCircleOutlined />}
              loading={batchSubmitMutation.isPending}
              onClick={() => batchSubmitMutation.mutate(batchRows)}
            >
              批量提交
            </Button>
          </div>
        </footer>

        <Modal
          title="Excel 校验结果"
          open={basicImportErrorsOpen}
          width={760}
          footer={
            <Button type="primary" onClick={() => setBasicImportErrorsOpen(false)}>
              知道了
            </Button>
          }
          onCancel={() => setBasicImportErrorsOpen(false)}
        >
          <Table<BasicProjectImportError>
            rowKey={(item) => `${item.row_number}-${item.field}-${item.code}`}
            size="small"
            dataSource={basicImportResult?.errors ?? []}
            pagination={{ pageSize: 8, hideOnSinglePage: true }}
            columns={[
              { title: "Excel 行", dataIndex: "row_number", width: 90 },
              {
                title: "字段",
                dataIndex: "field",
                width: 150,
                render: (field: string) => BASIC_IMPORT_FIELD_LABELS[field] ?? field,
              },
              {
                title: "原值",
                dataIndex: "raw_value",
                width: 180,
                ellipsis: true,
                render: displayImportValue,
              },
              { title: "问题", dataIndex: "reason" },
            ]}
          />
        </Modal>

        {renderPartyPickerModal()}
      </main>
    );
  }

  return (
    <main className="management-page project-management-page">
      <div className="page-heading project-page-heading">
        <div>
          <Typography.Title level={1}>{isAdmin ? "全部项目" : "我的项目"}</Typography.Title>
          <Typography.Paragraph type="secondary">
            {isAdmin ? "查看和管理所有用户提交的版权登记项目" : "管理您提交的版权登记资料与附件"}
          </Typography.Paragraph>
        </div>
        {!isAdmin ? (
          <Button
            className="project-create-button"
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}
          >
            提交登记
          </Button>
        ) : null}
      </div>

      <ProjectLibraryView
        isAdmin={Boolean(isAdmin)}
        recycleBin={recycleBin}
        loading={listQuery.isPending}
        projects={listQuery.data?.items ?? []}
        total={listQuery.data?.total ?? 0}
        page={page}
        counts={{
          all:
            statusFilter === undefined
              ? (listQuery.data?.total ?? 0)
              : (allCountQuery.data?.total ?? 0),
          submitted:
            statusFilter === "SUBMITTED"
              ? (listQuery.data?.total ?? 0)
              : (submittedCountQuery.data?.total ?? 0),
          completed:
            statusFilter === "COMPLETED"
              ? (listQuery.data?.total ?? 0)
              : (completedCountQuery.data?.total ?? 0),
        }}
        filters={{
          searchInput,
          status: statusFilter,
          workType: workTypeFilter,
          ownerUserId,
          reviewAssignmentStatus,
          submittedDate,
        }}
        workTypes={metadataQuery.data?.work_types ?? []}
        workTypeLabels={workTypeLabels}
        owners={ownersQuery.data?.items ?? []}
        selectedProjectIds={selectedProjectIds}
        batchCompleting={projectBatchCompleteMutation.isPending}
        batchDeleting={projectBatchDeleteMutation.isPending}
        onSearchInputChange={setSearchInput}
        onApplySearch={() => {
          setSearch(searchInput.trim());
          setPage(1);
          setSelectedProjectIds([]);
        }}
        onResetFilters={resetFilters}
        onStatusChange={(value) => {
          setStatusFilter(value);
          setPage(1);
          setSelectedProjectIds([]);
        }}
        onWorkTypeChange={(value) => {
          setWorkTypeFilter(value);
          setPage(1);
          setSelectedProjectIds([]);
        }}
        onOwnerChange={(value) => {
          setOwnerUserId(value);
          setPage(1);
          setSelectedProjectIds([]);
        }}
        onReviewAssignmentStatusChange={(value) => {
          setReviewAssignmentStatus(value);
          setPage(1);
          setSelectedProjectIds([]);
        }}
        onSubmittedDateChange={(value) => {
          setSubmittedDate(value);
          setPage(1);
          setSelectedProjectIds([]);
        }}
        onToggleRecycleBin={() => {
          setRecycleBin((value) => !value);
          setPage(1);
          setSelectedProjectIds([]);
        }}
        onToggleCurrentPage={toggleCurrentPage}
        onOpenAssignment={() => setAssignmentOpen(true)}
        onBatchComplete={confirmBatchComplete}
        onBatchDelete={confirmBatchDelete}
        onSelectedProjectIdsChange={setSelectedProjectIds}
        onPageChange={(nextPage) => {
          setPage(nextPage);
          setSelectedProjectIds([]);
        }}
        onCreate={openCreate}
        onSelectProject={selectProject}
        onView={(record) => void navigate(projectDetailPath(record))}
        onEdit={(record) => void navigate(`${projectDetailPath(record)}?edit=1`)}
        onChangeStatus={(record, status) => void changeStatus(record, status)}
        onDelete={confirmRemove}
        onRestore={(record) => void restore(record)}
      />

      <ReviewerAssignmentModal
        open={assignmentOpen}
        projectCount={selectedProjectIds.length}
        reviewers={reviewersQuery.data ?? []}
        loading={reviewersQuery.isPending}
        submitting={assignmentMutation.isPending}
        onClose={() => setAssignmentOpen(false)}
        onConfirm={(reviewerIds) => assignmentMutation.mutate(reviewerIds)}
      />

      <Modal
        className="registration-choice-modal"
        centered
        width={720}
        open={choiceOpen}
        footer={null}
        onCancel={() => setChoiceOpen(false)}
        title={null}
      >
        <div className="registration-choice-title">
          <Typography.Title level={2}>选择登记方式</Typography.Title>
          <Typography.Paragraph type="secondary">请选择本次版权登记的创建方式</Typography.Paragraph>
        </div>
        <div className="registration-choice-grid">
          <button className="registration-choice-card" type="button" onClick={openSingleCreate}>
            <span className="registration-choice-icon">
              <FileTextOutlined />
            </span>
            <strong>单项目登记</strong>
            <small>适合登记单个作品，填写项目信息并上传相关材料</small>
            <em>预计 2-3 分钟完成</em>
            <b>创建单项目</b>
          </button>
          <button
            className="registration-choice-card registration-choice-card--primary"
            type="button"
            onClick={openBatchChoice}
          >
            <span className="registration-choice-icon">
              <TableOutlined />
            </span>
            <strong>
              批量登记 <i>推荐</i>
            </strong>
            <small>适合一次登记多个作品，支持在线表格填写或 Excel 导入</small>
            <em>可批量设置公共信息与匹配附件</em>
            <b>开始批量登记</b>
          </button>
        </div>
        <div className="registration-choice-note">
          <CheckCircleOutlined />
          两种方式创建的项目均独立保存，并记录完整操作日志
        </div>
      </Modal>

      <Modal
        className="registration-choice-modal batch-choice-modal"
        centered
        width={730}
        open={batchChoiceOpen}
        footer={null}
        onCancel={() => setBatchChoiceOpen(false)}
        title={null}
      >
        <button
          className="choice-back-link"
          type="button"
          onClick={() => {
            setBatchChoiceOpen(false);
            setChoiceOpen(true);
          }}
        >
          <LeftOutlined /> 返回
        </button>
        <div className="registration-choice-title">
          <Typography.Title level={2}>选择批量录入方式</Typography.Title>
          <Typography.Paragraph type="secondary">
            选择一种方式录入多个版权登记项目
          </Typography.Paragraph>
        </div>
        <div className="registration-choice-grid">
          <button
            className="registration-choice-card registration-choice-card--primary"
            type="button"
            onClick={openBatchWorkspace}
          >
            <span className="registration-choice-icon">
              <FormOutlined />
            </span>
            <strong>
              在线表格填写 <i>推荐</i>
            </strong>
            <small>直接在页面中批量录入，支持新增、复制行和粘贴 Excel 多行数据</small>
            <ul>
              <li>适合 30 个以内项目</li>
              <li>实时校验必填信息</li>
              <li>本机自动保存填写进度</li>
            </ul>
            <b>进入在线填写</b>
          </button>
          <button className="registration-choice-card" type="button" onClick={openBatchWorkspace}>
            <span className="registration-choice-icon registration-choice-icon--green">
              <FileExcelOutlined />
            </span>
            <strong>导入表格文件</strong>
            <small>下载标准模板填写后上传，系统将解析并生成项目数据</small>
            <ul>
              <li>适合大量项目</li>
              <li>支持 .xlsx 文件</li>
              <li>导入后可在线修改</li>
            </ul>
            <b>下载模板并导入</b>
          </button>
        </div>
        <div className="registration-choice-note">
          <InfoCircleOutlined />
          两种方式均会进入在线表格确认，校验通过后才会正式创建项目
        </div>
      </Modal>

      <Modal
        className="project-form-modal"
        title={null}
        width={1120}
        open={formOpen}
        centered
        destroyOnHidden
        onCancel={() => setFormOpen(false)}
        footer={
          <div className="project-form-footer">
            <span>带 * 为必填项</span>
            <Button onClick={() => setFormOpen(false)}>取消</Button>
            <Button
              className="project-form-submit"
              type="primary"
              icon={<PlusOutlined />}
              loading={saveMutation.isPending}
              onClick={() => form.submit()}
            >
              创建项目
            </Button>
          </div>
        }
      >
        <div className="project-form-title">
          <span aria-hidden="true" />
          <div>
            <Typography.Title level={2}>创建版权登记项目</Typography.Title>
            <Typography.Paragraph type="secondary">
              填写作品、登记人和其他必要信息后创建项目
            </Typography.Paragraph>
          </div>
        </div>
        <Form<ProjectFormValues>
          form={form}
          layout="vertical"
          requiredMark
          onFinish={(values) => saveMutation.mutate(values)}
        >
          <div className="project-form-layout">
            <section className="project-form-section project-form-section--main">
              <div className="project-form-section-title">
                <strong>基础信息</strong>
                <span>作品、日期、登记人</span>
              </div>
              <div className="form-grid">
                <Form.Item
                  className="span-2"
                  name="work_name"
                  label="作品名称"
                  rules={[{ required: true, max: 200 }]}
                >
                  <Input placeholder="请输入作品名称" />
                </Form.Item>
                <Form.Item name="work_type_code" label="作品类型" rules={[{ required: true }]}>
                  <Select
                    options={metadataQuery.data?.work_types.map((item) => ({
                      value: item.code,
                      label: item.label,
                    }))}
                  />
                </Form.Item>
                <Form.Item
                  name="rights_mode_code"
                  label="权利归属方式"
                  rules={[{ required: true }]}
                >
                  <Select
                    options={metadataQuery.data?.rights_modes.map((item) => ({
                      value: item.code,
                      label: item.label,
                    }))}
                    onChange={updateSingleRightsMode}
                  />
                </Form.Item>
                <Form.Item
                  name="completion_date"
                  label="创作完成日期"
                  rules={[{ required: true }]}
                  getValueProps={(value?: string) => ({ value: value ? dayjs(value) : null })}
                  getValueFromEvent={(value: Dayjs | null) => value?.format("YYYY-MM-DD") ?? ""}
                >
                  <DatePicker
                    allowClear
                    maxDate={dayjs()}
                    format="YYYY-MM-DD"
                    placeholder="请选择"
                  />
                </Form.Item>
                <Form.Item
                  name="publication_date"
                  label="创作发表日期"
                  getValueProps={(value?: string) => ({ value: value ? dayjs(value) : null })}
                  getValueFromEvent={(value: Dayjs | null) => value?.format("YYYY-MM-DD") ?? ""}
                >
                  <DatePicker
                    allowClear
                    maxDate={dayjs()}
                    minDate={singleCompletionDate ? dayjs(singleCompletionDate) : undefined}
                    format="YYYY-MM-DD"
                    placeholder="不填表示未发表"
                  />
                </Form.Item>
                <Form.Item
                  className="span-2"
                  name="right_holder_ids"
                  label="著作权人"
                  rules={[
                    { required: true, type: "array", min: 1, message: "请选择著作权人" },
                    {
                      validator: (_, value: string[] = []) => {
                        const limit = getProjectPartyLimit(
                          (form.getFieldValue("rights_mode_code") as string | undefined) ?? "",
                        );
                        return value.length <= limit
                          ? Promise.resolve()
                          : Promise.reject(new Error(`当前权利归属方式最多选择 ${limit} 人`));
                      },
                    },
                  ]}
                >
                  <PartySelectionControl
                    title="著作权人"
                    partyNameById={partyNameById}
                    onOpen={() => openSinglePartyPicker("right_holder_ids")}
                  />
                </Form.Item>
                <Form.Item
                  className="span-2"
                  name="author_ids"
                  label="作者"
                  rules={[
                    { required: true, type: "array", min: 1, message: "请选择作者" },
                    {
                      validator: (_, value: string[] = []) => {
                        const limit = getProjectPartyLimit(
                          (form.getFieldValue("rights_mode_code") as string | undefined) ?? "",
                        );
                        return value.length <= limit
                          ? Promise.resolve()
                          : Promise.reject(new Error(`当前权利归属方式最多选择 ${limit} 人`));
                      },
                    },
                  ]}
                >
                  <PartySelectionControl
                    title="作者"
                    partyNameById={partyNameById}
                    onOpen={() => openSinglePartyPicker("author_ids")}
                  />
                </Form.Item>
              </div>
            </section>
            <section className="project-form-section project-form-section--side">
              <div className="project-form-section-title">
                <strong>其他信息</strong>
                <span>简介、过程、网盘</span>
              </div>
              <Form.Item name="cloud_drive_info" label="网盘信息">
                <Input.TextArea
                  maxLength={500}
                  showCount
                  rows={1}
                  placeholder="请输入网盘分享链接、提取码或访问说明"
                />
              </Form.Item>
              <Form.Item
                name="work_summary"
                label="作品简介"
                rules={[
                  { required: true, whitespace: true, message: "请输入作品简介" },
                  {
                    max: PROJECT_LONG_TEXT_LIMIT,
                    message: `最多输入 ${PROJECT_LONG_TEXT_LIMIT} 字`,
                  },
                ]}
              >
                <Input.TextArea
                  maxLength={PROJECT_LONG_TEXT_LIMIT}
                  showCount
                  rows={4}
                  placeholder="请说明作品的主要内容、主题、表现形式和创作意图，建议字数在 200 字左右。"
                />
              </Form.Item>
              <Form.Item
                name="creation_process"
                label="创作过程"
                rules={[
                  { required: true, whitespace: true, message: "请输入创作过程" },
                  {
                    max: PROJECT_LONG_TEXT_LIMIT,
                    message: `最多输入 ${PROJECT_LONG_TEXT_LIMIT} 字`,
                  },
                ]}
              >
                <Input.TextArea
                  maxLength={PROJECT_LONG_TEXT_LIMIT}
                  showCount
                  rows={4}
                  placeholder="请说明创作起因、构思过程、主要工具或方法、修改完善过程，建议字数在 200 字左右。"
                />
              </Form.Item>
            </section>
          </div>
        </Form>
      </Modal>

      {renderPartyPickerModal()}
    </main>
  );
}
